import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <Layout>
        <div className="pt-20 pb-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-search-line text-2xl text-foreground-300"></i>
            </div>
            <h2 className="text-xl font-semibold text-foreground-900 mb-2">文章未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">该文章可能已被删除或链接无效</p>
            <Link
              to="/articles"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 font-medium rounded-full text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回文章列表
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const canonicalUrl = `/articles/${article.slug}`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '新有菜',
    },
    url: `https://www.xinyoucai.com${canonicalUrl}`,
  };

  const relatedArticles = articles.filter((a) => a.id !== article.id).slice(0, 3);

  return (
    <Layout>
      <SEO
        title={`${article.title} - 新有菜健康资讯`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={canonicalUrl}
        ogType="article"
        ogImage={article.coverImage}
        schema={[articleSchema]}
      />

      <article className="pt-20 pb-16">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '健康资讯', href: '/articles' },
              { label: article.title },
            ]}
          />

          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 bg-primary-50 text-primary-700 text-xs font-medium rounded-full">
                {article.category}
              </span>
              <span className="text-xs text-foreground-400">{article.readTime}</span>
              <span className="text-xs text-foreground-300">{article.publishDate}</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center">
                <i className="ri-user-line text-sm text-secondary-600"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground-800">{article.author}</p>
              </div>
            </div>
          </header>

          <div className="aspect-[16/9] rounded-xl overflow-hidden mb-8">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none
              prose-headings:font-heading prose-headings:text-foreground-950 prose-headings:font-semibold
              prose-h2:text-xl prose-h2:md:text-2xl prose-h2:mt-8 prose-h2:mb-4
              prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
              prose-strong:text-foreground-900
              text-foreground-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-8 pt-6 border-t border-background-200/70">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-secondary-50 text-secondary-700 text-xs rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <div className="mt-12 pt-8 border-t border-background-200/70">
              <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-5">相关文章</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/articles/${ra.slug}`}
                    className="group bg-background-100 rounded-xl overflow-hidden hover:bg-background-200/50 transition-colors cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-3">
                      <p className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {ra.title}
                      </p>
                      <p className="text-xs text-foreground-400 mt-1">{ra.publishDate}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </Layout>
  );
}